import type React from 'react';
import { createSlot } from '@ayocore/platform/client/extensions/createSlot';
import type { LeadExtended } from './LeadViewController';

/** Payload, передаваемый во все слоты LeadView */
export interface LeadViewPayload {
  lead: LeadExtended;
  /** Slug типа лида (из авто-JOIN репозитория). Позволяет расширениям не делать лишний RPC-запрос. */
  leadTypeSlug: string | null;
  reload: () => void;
}

/** Описание дополнительной вкладки */
export interface LeadTabExtension {
  /** Уникальный ключ вкладки (не должен совпадать с 'main', 'rows') */
  value: string;
  /** Отображаемый заголовок вкладки */
  label: string;
  /** Содержимое панели вкладки */
  content: React.ReactNode;
}

/**
 * Точки расширения LeadView.
 *
 * Позволяют клиентским модулям добавлять контент в LeadView
 * без изменения базового кода CRM модуля.
 *
 * Регистрация происходит в bootstrap.ts клиентского модуля.
 *
 * @example
 * // src/modules/grasias/client/bootstrap.ts
 * import { LeadViewExtensions } from '@ayocore/erp/crm/client/components/Leads/views/LeadViewExtensions';
 *
 * // Дополнительная вкладка
 * LeadViewExtensions.tabs.register(({ lead, reload }) => ({
 *   value: 'grasias-deals',
 *   label: 'Сделки',
 *   content: <GrasiasLeadDeals lead={lead} onSuccess={reload} />,
 * }));
 *
 * // Кнопка в заголовке
 * LeadViewExtensions.headerActions.register(({ lead, reload }) => (
 *   <GrasiasLeadButton lead={lead} onSuccess={reload} />
 * ));
 *
 * // Блок в основной вкладке
 * LeadViewExtensions.mainPanelBlocks.register(({ lead }) => (
 *   <GrasiasLeadInfo lead={lead} />
 * ));
 */
export const LeadViewExtensions = {
  /** Дополнительные кнопки в заголовке (рядом с меню действий) */
  headerActions: createSlot<LeadViewPayload>(),

  /** Дополнительные пункты в раскрывающемся меню действий (Menu.Item) */
  menuActions: createSlot<LeadViewPayload>(),

  /** Дополнительные вкладки (добавляются после базовых) */
  tabs: createSlot<LeadViewPayload, LeadTabExtension>(),

  /** Дополнительные блоки в конце основной вкладки */
  mainPanelBlocks: createSlot<LeadViewPayload>(),

  /** Скрыть правую панель с лентой событий */
  hideEventList: createSlot<LeadViewPayload, boolean>(),
};
